import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  popoverBoundedMaxWidthClass,
} from '../ui/popover';
import { FieldGroup } from '../forms/FieldGroup';

export interface ConfigChipFieldProps {
  /** Caption shown above the chip. */
  label: string;
  /**
   * Optional docs slug (with optional `#anchor`) for the trailing
   * `InlineDocsHint`. When omitted the hint is not rendered.
   */
  docsSlug?: string;
  /** Extra classes on the field wrapper (e.g. mobile full-width chip triggers). */
  className?: string;
  children: ReactNode;
}

/**
 * Captions a single config chip with a small label above the control and
 * pushes an optional inline docs hint to the end of the caption row, so a row
 * of chips reads as a set of named fields and the docs affordance sits flush
 * at the end of each label bar rather than crowding the caption text.
 *
 * A thin specialization of {@link FieldGroup} (the shared dense-caption field
 * block): it pins `min-w-0` so the chip label truncates inside the flex-row
 * chip layouts the launcher's Advanced bar uses.
 */
export function ConfigChipField({ label, docsSlug, className, children }: ConfigChipFieldProps) {
  return (
    <FieldGroup label={label} docsSlug={docsSlug} className={cn('min-w-0', className)}>
      {children}
    </FieldGroup>
  );
}

export interface ConfigChipProps {
  ariaLabel: string;
  /** Chip summary text/content rendered beside the icon. */
  label: ReactNode;
  icon: ReactNode;
  /** Renders the chip with a dashed outline + muted text (e.g. "add" affordance). */
  dashed?: boolean;
  /**
   * Disables the trigger so the popover can't open. Use for a control that is
   * contextually unavailable (e.g. a base blueprint can't build on top of
   * another blueprint), keeping the chip visible as a read-only summary.
   */
  disabled?: boolean;
  /** Editor content rendered inside the click popover; receives a `close` fn. */
  children: (close: () => void) => ReactNode;
  className?: string;
  /** Extra classes for the popover surface (e.g. raise the `min-w`/`max-w` floor). */
  popoverClassName?: string;
  /**
   * Whether opening the popover moves focus into its content (Radix default).
   * Leave `true` for editors whose first element should grab focus (e.g. a
   * search input). Set `false` when the first child is a passive control that
   * shouldn't be auto-focused — notably a hover-card trigger, which Radix opens
   * on focus and would otherwise pop open the moment the popover appears.
   */
  autoFocusContent?: boolean;
  'data-testid'?: string;
}

/**
 * A chip trigger that opens an editor `Popover` on click — the shared affordance
 * used by the Advanced, Attachments, and provider-key chip bars.
 */
export function ConfigChip({
  ariaLabel,
  label,
  icon,
  dashed,
  disabled,
  children,
  className,
  popoverClassName,
  autoFocusContent = true,
  'data-testid': dataTestId,
}: ConfigChipProps) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={disabled ? false : open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-label={ariaLabel}
          data-testid={dataTestId}
          disabled={disabled}
          className={cn(
            // `w-fit` keeps the chip sized to its content so it never stretches
            // to fill a captioned field that sits in a full-width column (the
            // caption wrapper is a `flex flex-col`, whose default
            // `align-items: stretch` would otherwise blow the chip out to full
            // width). `max-w-full` still caps + truncates long labels.
            'h-7 w-fit max-w-full justify-start gap-1.5 px-2 text-2xs font-normal',
            dashed && 'border-dashed text-muted-foreground',
            className,
          )}
        >
          {icon}
          <span className="truncate">{label}</span>
          <ChevronDown className="ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn(
          'w-auto min-w-[12rem] p-0',
          popoverBoundedMaxWidthClass('20rem'),
          popoverClassName,
        )}
        onOpenAutoFocus={autoFocusContent ? undefined : (event) => event.preventDefault()}
      >
        {children(() => setOpen(false))}
      </PopoverContent>
    </Popover>
  );
}
